
// Home.js
import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import Petrinet, { updatePetrinetOffset } from "./Petrinet";
import Executions from "./Executions";
import Obligations from "./Obligations";
import FilePopup from "./FilePopup";
import Tree, { updateTreeOffset } from "./Tree";

/* =========================
   HOISTED, MEMOIZED VIEWS
   ========================= */

/** Top navigation bar (File/Analyse tabs + tab switcher) */
const MenuView = React.memo(function MenuView({
  activeTab,
  setActiveTab,
  uploadedPetrinet,
  uploadedAnnotations,
  canAnalyse,
  canVisualise,
  visualisationReady,
  showFileMenu,
  setShowFileMenu,
  showAnalyseMenu,
  setShowAnalyseMenu,
  fileMenuRef,
  analyseMenuRef,
  onFileClick,        // stable
  onAnalysisClick,    // stable
}) {

  return (
    <div>
      <div
        style={{
          background: "#333",
          padding: "10px",
          color: "white",
          display: "flex",
          gap: "20px",
          position: "relative",
          alignItems: "center",
        }}
      >
        <div style={{ fontWeight: "bold" }}>DAMOCLES™</div>

        {/* File menu (only in model tab) */}
        {activeTab === "model" && (
          <div ref={fileMenuRef} style={{ position: "relative" }}>
            <span
              style={{ cursor: "pointer" }}
              onClick={() => {
                setShowFileMenu(!showFileMenu);
                setShowAnalyseMenu(false); // close Analyse if File is opened
              }}
            >
              File ▼
            </span>
            {showFileMenu && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  background: "white",
                  color: "black",
                  marginTop: "5px",
                  padding: "5px",
                  border: "1px solid #ccc",
                  zIndex: 1,
                  whiteSpace: "nowrap",
                  width: "auto",
                  minWidth: "max-content",
                }}
              >
                <div
                  style={{ padding: "5px", cursor: "pointer" }}
                  onClick={() => onFileClick("uploadPetrinet")}
                >
                  Upload petrinet
                </div>
                <div
                  style={{
                    padding: "5px",
                    cursor: uploadedPetrinet ? "pointer" : "not-allowed",
                    opacity: uploadedPetrinet ? 1 : 0.5,
                  }}
                  onClick={() =>
                    uploadedPetrinet && onFileClick("uploadAnnotations")
                  }
                >
                  Upload annotations
                </div>
                <div
                  style={{
                    padding: "5px",
                    cursor: uploadedAnnotations ? "pointer" : "not-allowed",
                    opacity: uploadedAnnotations ? 1 : 0.5,
                  }}
                  onClick={() =>
                    uploadedAnnotations && onFileClick("uploadObligations")
                  }
                >
                  Upload obligations
                </div>
                <div
                  style={{
                    padding: "5px",
                    cursor: uploadedPetrinet ? "pointer" : "not-allowed",
                    opacity: uploadedPetrinet ? 1 : 0.5,
                  }}
                  onClick={() => uploadedPetrinet && onAnalysisClick("clear")}
                >
                  Clear
                </div>
              </div>
            )}
          </div>
        )}

        {/* Analyse menu (only in model tab) */}
        {activeTab === "model" && (
          <div ref={analyseMenuRef} style={{ position: "relative" }}>
            <span
              style={{
                cursor: canAnalyse ? "pointer" : "not-allowed",
                opacity: canAnalyse ? 1 : 0.5,
              }}
              onClick={() => {
                if (canAnalyse) {
                  setShowAnalyseMenu(!showAnalyseMenu);
                  setShowFileMenu(false);
                }
              }}
            >
              Analyse ▼
            </span>
            {showAnalyseMenu && canAnalyse && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  background: "white",
                  color: "black",
                  marginTop: "5px",
                  padding: "5px",
                  border: "1px solid #ccc",
                  zIndex: 1,
                  whiteSpace: "nowrap",
                  width: "auto",
                  minWidth: "max-content",
                }}
              >
                <div
                  style={{ padding: "5px", cursor: "pointer" }}
                  onClick={() => onAnalysisClick("fastFail")}
                >
                  Fast fail
                </div>
                <div
                  style={{
                    padding: "5px",
                    cursor: canVisualise ? "pointer" : "not-allowed",
                    opacity: canVisualise ? 1 : 0.5,
                  }}
                  onClick={() => {
                    if (!canVisualise) return;
                    onAnalysisClick("visualise");
                  }}
                >
                  Visualisation
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tab buttons */}
        <div style={{ position: "relative" }}>
          {activeTab === "visualisation" && (
            <div
              style={{ cursor: "pointer" }}
              onClick={() => setActiveTab("model")}
            >
              <span style={{ marginLeft: 4 }}>←</span>Model
            </div>
          )}
          {activeTab === "model" && visualisationReady && (
            <div
              style={{ cursor: "pointer" }}
              onClick={() => setActiveTab("visualisation")}
            >
              Visualisation<span style={{ marginLeft: 4 }}>→</span>
            </div>
          )}
        </div>

        {/* Right aligned help link */}
        <li style={{ marginLeft: "auto", listStyle: "none" }}>
          <Link to="/help" style={{ color: "white", textDecoration: "none" }}>
            Help
          </Link>
        </li>
      </div>
    </div>
  );
});

/** Left-side model canvas (Petri net) with spinner */
const ModelView = React.memo(function ModelView({
  loading,
  petrinet,
  offsetPetrinet,
  setOffsetPetrinet,
  zoom,
  setZoom,
}) {
  // Compute offset once the graph is available
  if (petrinet && offsetPetrinet.x == null) {
    // set offset value outside window until we've fully rendered
    updatePetrinetOffset(null, petrinet, zoom, offsetPetrinet, setOffsetPetrinet);
  }

  // wait until we've generated offset
  if (petrinet && offsetPetrinet.x == null) return <div />;

  return (
    <div style={{ height: "100vh", overflow: "hidden" }}>
      {loading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
            pointerEvents: "all",
            backgroundColor: "rgba(255, 255, 255, 0.3)",
          }}
        >
          <div
            style={{
              width: "40px",
              height: "40px",
              border: "4px solid #ccc",
              borderTop: "4px solid #333",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          />
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      )}
      {/* Left side: Graph */}
      <div style={{ width: "100%" }}>
        <Petrinet
          petrinet={petrinet}
          setPetrinet={() => {}}
          offsetPetrinet={offsetPetrinet}
          setOffsetPetrinet={setOffsetPetrinet}
          zoom={zoom}
          setZoom={setZoom}
        />
      </div>
    </div>
  );
});

/** Right-side visualisation canvas (Tree) */
const VisualisationView = React.memo(function VisualisationView({
  treeData,
  zoomTree,
  setZoomTree,
  offsetTree,
  setOffsetTree,
}) {
  if (treeData && offsetTree.x == null) {
    // set offset value outside window until we've fully rendered
    updateTreeOffset(null, treeData, 0, 0, zoomTree, offsetTree, setOffsetTree);
  }

  if (treeData && offsetTree.x == null) return <div />;

  return (
    <div style={{ height: "100%", overflow: "hidden" }}>
      {treeData && (
        <Tree
          tree={treeData}
          nodeSize={30}
          zoomTree={zoomTree}
          setZoomTree={setZoomTree}
          offsetTree={offsetTree}
          setOffsetTree={setOffsetTree}
        />
      )}
    </div>
  );
});

/** Right column with executions and obligations */
const TableView = React.memo(function TableView({
  executions,
  selectedExecution,
  setSelectedExecution,
  obligations,
  selectedObligation,
  setSelectedObligation,
  handleFairnessSelect,
}) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        background: "rgb(247,247,247)",
        height: "90vh",
        width: "25%",
        padding: "10px",
      }}
    >
      <div style={{ overflowY: "auto", height: "50%", padding: "10px" }}>
        <Executions
          executions={executions}
          selectedExecution={selectedExecution}
          setSelectedExecution={setSelectedExecution}
          handleFairnessSelect={handleFairnessSelect}
        />
      </div>
      <div style={{ overflowY: "auto", height: "50%", padding: "10px" }}>
        <Obligations
          obligations={obligations}
          selectedObligation={selectedObligation}
          setSelectedObligation={setSelectedObligation}
        />
      </div>
    </div>
  );
});

/* =========================
   MAIN PAGE COMPONENT
   ========================= */

function Home() {
  const [loading, setLoading] = useState(false);

  // Menu state
  const [showFileMenu, setShowFileMenu] = useState(false);
  const [showAnalyseMenu, setShowAnalyseMenu] = useState(false);
  const fileMenuRef = useRef(null);
  const analyseMenuRef = useRef(null);

  // Upload status and analysis availability
  const [uploadedPetrinet, setUploadedPetrinet] = useState(false);
  const [uploadedAnnotations, setUploadedAnnotations] = useState(false);
  const [canAnalyse, setCanAnalyse] = useState(false);
  const [canVisualise, setCanVisualise] = useState(false);
  const [visualisationReady, setVisualisationReady] = useState(false);

  // File popup
  const [showFilePopup, setShowFilePopup] = useState(false);
  const [fileType, setFileType] = useState("");
  const [accept, setAccept] = useState("");

  // Model state
  const [petrinet, setPetrinet] = React.useState(null);
  const [offsetPetrinet, setOffsetPetrinet] = useState({ x: null, y: null });
  const [zoom, setZoom] = useState(1); // 1 = 100%

  // Executions/Obligations state
  const [executions, setExecutions] = React.useState(null);
  const [selectedExecution, setSelectedExecution] = React.useState(null);
  const [obligations, setObligations] = React.useState(null);
  const [selectedObligation, setSelectedObligation] = React.useState(null);

  // Tabs
  const [activeTab, setActiveTab] = useState("model"); // "model" or "visualisation"

  // Visualisation state
  const [treeData, setTreeData] = React.useState(null);
  const [offsetTree, setOffsetTree] = useState({ x: null, y: null });
  const [zoomTree, setZoomTree] = useState(1);

  /* -------- Global click outside to close menus -------- */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        fileMenuRef.current &&
        !fileMenuRef.current.contains(event.target) &&
        analyseMenuRef.current &&
        !analyseMenuRef.current.contains(event.target)
      ) {
        setShowFileMenu(false);
        setShowAnalyseMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  /* -------- Server interactions (stable callbacks) -------- */

  const showFileOptions = useCallback(async () => {
    const formData = new FormData();
    const response = await fetch(
      process.env.REACT_APP_API_URL + "/fileOptions",
      {
        method: "POST",
        body: formData,
        credentials: "include",
      }
    );
    const result = await response.json();
    setUploadedPetrinet(result.uploadedPetrinet);
    setUploadedAnnotations(result.uploadedAnnotations);
    setCanAnalyse(result.canAnalyse);
    setCanVisualise(result.canVisualise);
    setVisualisationReady(result.visualisationReady);
  }, []);

  const showModel = useCallback(async () => {
    const formData = new FormData();
    // Model + executions
    let response = await fetch(process.env.REACT_APP_API_URL + "/getModel", {
      method: "POST",
      body: formData,
      credentials: "include",
    });
    let data = await response.json();
    if (data.nodes != null) {
      // If petrinet actually changed, reset offset
      const changed = JSON.stringify(data) !== JSON.stringify(petrinet);
      setPetrinet(data);
      setExecutions(data.executions);
      if (changed) {
        setOffsetPetrinet({ x: null, y: null });
        setZoom(1);
      }
    } else {
      setPetrinet(null);
      setExecutions(null);
    }

    // Obligations
    response = await fetch(
      process.env.REACT_APP_API_URL + "/getObligations",
      {
        method: "POST",
        body: formData,
        credentials: "include",
      }
    );
    data = await response.json();
    setObligations(data.obligations);
  }, []);

  const handleFairnessSelect = useCallback(async (value) => {
    try {
      const formData = new FormData();
      formData.append("fairness", value);
      const response = await fetch(
        process.env.REACT_APP_API_URL + "/setFairness",
        {
          method: "POST",
          body: formData,
          credentials: "include",
        }
      );
      if (!response.ok) {
        console.error("Server error:", response);
        return;
      }
      // Refresh model after server-side fairness change
      setCanVisualise(true);
      await showModel();
    } catch (err) {
      console.error("Fetch failed:", err);
    }
  }, [showModel]);

  /* -------- File menu actions -------- */

  const onFileClick = useCallback((type) => {
    setFileType(type);
    // Set allowed extensions based on choice
    if (type === "uploadPetrinet") setAccept(".pnml");
    else if (type === "uploadAnnotations") setAccept(".xml");
    else if (type === "uploadObligations") setAccept(".xml");

    // Open the custom popup
    setTimeout(() => setShowFilePopup(true), 0);
    setShowFileMenu(false);
  }, []);

  const onAnalysisClick = useCallback(
    async (type) => {
      if (type === "fastFail") {
        const formData = new FormData();
        setLoading(true);
        try {
          let response = await fetch(
            process.env.REACT_APP_API_URL + "/fastFail",
            {
              method: "POST",
              body: formData,
              credentials: "include",
            }
          );
          if (response.ok) {
            let data = await response.json();
            setLoading(false);
            setTimeout(() => {
              alert(
                "Compliant: " + data.compliant + "\nFound in: " + data.time + "ms"
              );
            }, 50);
          } else {
            const error = await response.text();
            alert("Upload failed due to error: " + error);
          }
        } catch (error) {
          console.error("Error uploading file:", error);
        }
        setLoading(false);
      }

      if (type === "visualise") {
        const formData = new FormData();
        setLoading(true);
        let response = await fetch(
          process.env.REACT_APP_API_URL + "/visualise",
          {
            method: "POST",
            body: formData,
            credentials: "include",
          }
        );
        if (response.ok) {
          setVisualisationReady(true);
        } else {
          const error = await response.text();
          alert("Upload failed due to error: " + error);
        }
        setLoading(false);
      }

      if (type === "clear") {
        const formData = new FormData();
        await fetch(process.env.REACT_APP_API_URL + "/clear", {
          method: "POST",
          body: formData,
          credentials: "include",
        });
        await showModel();
        await showFileOptions();
      }

      setShowAnalyseMenu(false);
    },
    [showModel, showFileOptions]
  );

  const handleFileSelect = useCallback(
    async (file) => {
      try {
        const response = await fetch(file.path); // e.g., "/assets/petrinet1.pnml"
        const blob = await response.blob(); // Binary data

        // Create FormData and append as a file
        const formData = new FormData();
        formData.append("file", blob, file.name); // file.name is important for MultipartFile
        formData.append("fileType", fileType); // send which menu option was chosen

        let uploadResp = await fetch(
          process.env.REACT_APP_API_URL + "/upload",
          {
            method: "POST",
            body: formData,
            credentials: "include",
          }
        );

        if (uploadResp.ok) {
          if (fileType === "uploadPetrinet") setZoom(1);
        } else {
          const error = await uploadResp.text();
          alert("Upload failed due to error: " + error);
        }

        await showModel();
        await showFileOptions();
      } catch (error) {
        console.error("Error uploading file:", error);
      }
    },
    [fileType, showModel, showFileOptions]
  );

  /* -------- Defaults for visualisation tab -------- */
  useEffect(() => {
    if (activeTab === "visualisation") {
      if (Array.isArray(executions) && executions.length > 0) {
      if (!selectedExecution || !executions.includes(selectedExecution)){
        setSelectedExecution(executions[0]);
        }
      }
      if (Array.isArray(obligations) && obligations.length > 0) {

       const hasSelectedObligation = Array.isArray(obligations) &&
        obligations.some(o => o.id === selectedObligation);

        if (!selectedObligation || !hasSelectedObligation){
        setSelectedObligation(obligations[0].id);
        }
      }
    }
  }, [activeTab, executions, obligations, selectedExecution, selectedObligation]);

  /* -------- Fetch visualisation when both are set -------- */
  useEffect(() => {
    const ready =
      activeTab === "visualisation" &&
      selectedExecution &&
      selectedObligation;

    if (!ready) return;

    const fetchData = async () => {
      try {
        const formData = new FormData();
        if (!selectedExecution | !selectedObligation) return;
        formData.append("execution", selectedExecution);
        formData.append("obligationID", selectedObligation);
        const response = await fetch(
          process.env.REACT_APP_API_URL + "/getVisualisation",
          {
            method: "POST",
            body: formData,
            credentials: "include",
          }
        );
        if (!response.ok) {
          const error = await response.text();
          alert("Visualisation failed: " + error);
          return;
        }
        const data = await response.json();
        // Only reset offset if the data actually changed
        const changed = JSON.stringify(data) !== JSON.stringify(treeData);
        setTreeData(data);
        if (changed) {
          setOffsetTree({ x: null, y: null });
        }
      } catch (err) {
        console.error(err);
        alert("Network or server error");
      }
    };
    fetchData();
  }, [activeTab, selectedExecution, selectedObligation, treeData, canVisualise]);

  /* -------- Initial load -------- */
  useEffect(() => {
    showModel();
  }, [showModel]);

  useEffect(() => {
    showFileOptions();
  }, [showFileOptions]);

  /* -------- Render -------- */
  return (
    <div style={{ height: "100vh", overflow: "hidden" }}>
      <MenuView
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        uploadedPetrinet={uploadedPetrinet}
        uploadedAnnotations={uploadedAnnotations}
        canAnalyse={canAnalyse}
        canVisualise={canVisualise}
        visualisationReady={visualisationReady}
        showFileMenu={showFileMenu}
        setShowFileMenu={setShowFileMenu}
        showAnalyseMenu={showAnalyseMenu}
        setShowAnalyseMenu={setShowAnalyseMenu}
        fileMenuRef={fileMenuRef}
        analyseMenuRef={analyseMenuRef}
        onFileClick={onFileClick}
        onAnalysisClick={onAnalysisClick}
      />

      {/* File popup is controlled from Home (keeps identity stable) */}
      {showFilePopup && (
        <FilePopup
          fileType={fileType}
          accept={accept}
          onSelect={(file) => {
            handleFileSelect(file);
            setShowFilePopup(false);
          }}
          onClose={() => setShowFilePopup(false)}
        />
      )}

      <div style={{ display: "flex", height: "90vh" }}>
        {/* Tab content */}
        <div style={{ width: "75%" }}>
          {activeTab === "model" && (
            <ModelView
              loading={loading}
              petrinet={petrinet}
              offsetPetrinet={offsetPetrinet}
              setOffsetPetrinet={setOffsetPetrinet}
              zoom={zoom}
              setZoom={setZoom}
            />
          )}
          {activeTab === "visualisation" && (
            <VisualisationView
              treeData={treeData}
              zoomTree={zoomTree}
              setZoomTree={setZoomTree}
              offsetTree={offsetTree}
              setOffsetTree={setOffsetTree}
            />
          )}
        </div>

        <TableView
          executions={executions}
          selectedExecution={selectedExecution}
          setSelectedExecution={setSelectedExecution}
          obligations={obligations}
          selectedObligation={selectedObligation}
          setSelectedObligation={setSelectedObligation}
          handleFairnessSelect={handleFairnessSelect}
        />
      </div>
    </div>
  );
}

export default Home;
